import { eventPhase } from './eventShell';
import type { EventSummary } from './types';

// "Event mode" (phones only): while an event the rep works is live, opening the app lands in it
// and the event menu offers the way out. Leaving holds for the rest of that day at the venue.

export interface EventModeViewer { id: string; admin: boolean }

// The list only returns a non-admin's own events (lead or roster). Admins see every event, so
// for them a roster membership has to come from the event itself (`representativeIds`).
export function onEventTeam(event: Pick<EventSummary, 'id' | 'leadId'>, viewer: EventModeViewer, rosters: Readonly<Record<string, readonly string[]>>): boolean {
  if (event.leadId === viewer.id || !viewer.admin) return true;
  return rosters[event.id]?.includes(viewer.id) ?? false;
}

// Admins' live events that need their roster read before membership is known.
export function rostersNeeded(events: readonly EventSummary[] | undefined, viewer: EventModeViewer | null, now: number): string[] {
  if (!events || !viewer?.admin) return [];
  return events.filter((e) => eventPhase(e, now) === 'eventlive' && e.leadId !== viewer.id).map((e) => e.id);
}

// The calendar day at the venue, so "the rest of the day" ends at the venue's midnight.
export function venueDay(timeZone: string | null | undefined, now: number): string {
  const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' };
  try {
    return new Intl.DateTimeFormat('en-CA', timeZone ? { ...options, timeZone } : options).format(now);
  } catch {
    return new Intl.DateTimeFormat('en-CA', options).format(now);
  }
}

const leftKey = (actorId: string, eventId: string) => `meridian:event-mode-left:${actorId}:${eventId}`;
const listeners = new Set<() => void>();
let leftVersion = 0;

export function subscribeEventModeLeft(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export const eventModeLeftVersion = () => leftVersion;

export function readEventModeLeft(actorId: string, eventId: string): string | null {
  try { return localStorage.getItem(leftKey(actorId, eventId)); } catch { return null; }
}

export function rememberEventModeLeft(actorId: string, event: Pick<EventSummary, 'id' | 'timeZone'>, now = Date.now()): void {
  try { localStorage.setItem(leftKey(actorId, event.id), venueDay(event.timeZone, now)); } catch { /* storage unavailable: best effort */ }
  leftVersion += 1;
  listeners.forEach((listener) => listener());
}

export function leftEventModeToday(leftDay: string | null, event: Pick<EventSummary, 'timeZone'>, now: number): boolean {
  return leftDay !== null && leftDay === venueDay(event.timeZone, now);
}

export interface EventModeInput {
  events: readonly EventSummary[];
  viewer: EventModeViewer;
  rosters: Readonly<Record<string, readonly string[]>>;
  // The venue day the rep last left event mode for each event on this device, if ever.
  leftDays: Readonly<Record<string, string | null>>;
  now: number;
}

// Live events this rep works that they haven't left today.
export function eventModeCandidates({ events, viewer, rosters, leftDays, now }: EventModeInput): EventSummary[] {
  return events.filter((e) => eventPhase(e, now) === 'eventlive' && onEventTeam(e, viewer, rosters)
    && !leftEventModeToday(leftDays[e.id] ?? null, e, now));
}

export function inEventMode(eventId: string, input: EventModeInput): EventSummary | null {
  return eventModeCandidates(input).find((e) => e.id === eventId) ?? null;
}

export type EventLanding = { to: string } | null;

// Where a cold open of the app's home goes. Only the PWA start URL (`/` with nothing after it)
// on the first route of a page load, on a phone, qualifies: a deep link, coming back to `/` from
// inside the app, or any laptop or tablet layout always stays put.
export function eventLanding({ coldLanding, phone, pathname, search, ...input }: EventModeInput & {
  coldLanding: boolean; phone: boolean; pathname: string; search: string;
}): EventLanding {
  if (!coldLanding || !phone || pathname !== '/' || search) return null;
  const candidates = eventModeCandidates(input);
  if (candidates.length === 1) return { to: `/events/${candidates[0].id}` };
  if (candidates.length > 1) return { to: '/events' };
  return null;
}
